import { tzOffset } from "@date-fns/tz";
import { Timezone } from "@hex/common";

import type { TimezoneWithNames } from "./timezoneTypes";

export const TimezoneDisplayFormat = {
  /**
   * Short name format: "HST", "EDT", etc.
   * Falls back to "GMT+/-offset" if there is no commonly used abbreviation.
   */
  ABBREVIATION: "abbreviation" as const,

  /**
   * IANA timezone code: "Pacific/Honolulu", "America/New_York", etc.
   */
  CODE: "code" as const,

  /**
   * Composite format: "Hawaii Time (HST) -10:00", "New York (EDT) -5:00", etc.
   * Omits abbreviation if there is no short name (it is redundant with offset).
   */
  COMPOSITE: "composite" as const,

  /**
   * Long name format: "Hawaii-Aleutian Standard Time", "Eastern Daylight Time", "Coordinated Universal Time", etc.
   */
  LONG_NAME: "long-name" as const,

  /**
   * Offset format: "-10:00", "-5:00", etc.
   */
  OFFSET: "offset" as const,
};

export type TimezoneDisplayFormat =
  (typeof TimezoneDisplayFormat)[keyof typeof TimezoneDisplayFormat];

export function formatOffset(timezone: Timezone): string {
  const offsetInMinutes = tzOffset(timezone.ianaCode, new Date());

  const isPositiveOffset = offsetInMinutes >= 0;
  const offsetHours = Math.trunc(Math.abs(offsetInMinutes) / 60)
    .toString()
    .padStart(2, "0");

  const offsetMinutes = (Math.abs(offsetInMinutes) % 60)
    .toString()
    .padStart(2, "0");

  return `${isPositiveOffset ? "+" : "-"}${offsetHours}:${offsetMinutes}`;
}

/**
 * Formats a timezone according to the specified display format to show in the default `<Button>` rendered as the
 * `<TimezoneSelect>` target element.
 */
export function formatTimezone(
  timezone: TimezoneWithNames | undefined,
  displayFormat: TimezoneDisplayFormat,
): string | undefined {
  if (timezone === undefined) {
    return undefined;
  }

  switch (displayFormat) {
    case TimezoneDisplayFormat.ABBREVIATION:
      return timezone.shortName;
    case TimezoneDisplayFormat.OFFSET:
      return formatOffset(timezone);
    case TimezoneDisplayFormat.CODE:
      return timezone.ianaCode;
    case TimezoneDisplayFormat.LONG_NAME:
      return timezone.longName;
    case TimezoneDisplayFormat.COMPOSITE:
      const offset = formatOffset(timezone);

      return /[-+]/.test(timezone.shortName) ||
        timezone.shortName === timezone.label
        ? `${timezone.label} ${offset}`
        : `${timezone.label} (${timezone.shortName}) ${offset}`;
    default:
      return undefined;
  }
}
